const getTierDiscountSubtotal = (items) => items.reduce(
  (total, item) => total + (item.gift_card ? 0 : Math.max(0, Number(item.original_line_price) || 0)), 0
);

window.NelpandaTierDiscountSubtotal = getTierDiscountSubtotal;

const getTierDiscountState = (totalCents, thresholds) => {
  const total = Math.max(0, Number(totalCents) || 0);
  let previousThreshold = 0;
  const progress = thresholds.map((threshold) => {
    const span = threshold - previousThreshold;
    const value = span > 0 ? Math.min(100, Math.max(0, (total - previousThreshold) * 100 / span)) : 100;
    previousThreshold = threshold;
    return value;
  });
  const nextIndex = thresholds.findIndex((threshold) => total < threshold);

  return {
    nextIndex,
    progress,
    remaining: nextIndex === -1 ? 0 : thresholds[nextIndex] - total
  };
};

window.NelpandaTierDiscountState = getTierDiscountState;

if (!customElements.get('tier-discount-bar')) {
  class TierDiscountBar extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-tier-viewport]');
      this.tiers = [...this.querySelectorAll('[data-tier]')];
      this.previousButton = this.querySelector('[data-tier-previous]');
      this.nextButton = this.querySelector('[data-tier-next]');
      this.status = this.querySelector('[data-tier-status]');
      this.handlePrevious = () => this.scrollByTier(-1);
      this.handleNext = () => this.scrollByTier(1);
      this.updateControls = this.updateControls.bind(this);

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.viewport?.addEventListener('scroll', this.updateControls, { passive: true });

      if (this.viewport && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.updateControls);
        this.resizeObserver.observe(this.viewport);
      }

      this.render();
      requestAnimationFrame(() => {
        this.positionToNextTier();
        this.updateControls();
      });
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.viewport?.removeEventListener('scroll', this.updateControls);
      this.resizeObserver?.disconnect();
    }

    render() {
      const thresholds = this.tiers.map((tier) => Number(tier.dataset.thresholdCents));
      const subtotal = getTierDiscountSubtotal(
        JSON.parse(this.dataset.cartItems || '[]')
      );
      const state = getTierDiscountState(subtotal, thresholds);

      this.tiers.forEach((tier, index) => {
        const unlocked = state.progress[index] >= 100;
        const progress = tier.querySelector('[data-tier-progress]');
        const fill = tier.querySelector('[data-tier-progress-fill]');
        const check = tier.querySelector('[data-tier-check]');

        tier.dataset.tierUnlocked = String(unlocked);
        if (progress) progress.setAttribute('aria-valuenow', String(Math.round(state.progress[index])));
        if (fill) fill.style.width = `${state.progress[index]}%`;
        if (check) check.hidden = !unlocked;
      });

      if (!this.status) return;
      if (state.nextIndex === -1) {
        this.append(this.status);
        this.status.textContent = this.dataset.completeMessage;
        this.status.classList.add('is-complete');
        return;
      }

      const nextTier = this.tiers[state.nextIndex];
      nextTier.append(this.status);
      this.status.replaceChildren(...nextTier.dataset.remainingMessage.split(/(__AMOUNT__)/).map((part) => {
        if (part !== '__AMOUNT__') return document.createTextNode(part);
        const value = document.createElement('span');
        value.className = 'tier-discount-bar__status-value';
        value.textContent = this.formatMoney(state.remaining);
        return value;
      }));
      this.status.classList.remove('is-complete');
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, this.dataset.moneyFormat);
      }
      return `¥${Math.round(cents / 100).toLocaleString('ja-JP')}`;
    }

    positionToNextTier() {
      if (!this.viewport || !this.tiers.length) return;
      const nextIndex = this.tiers.findIndex((tier) => tier.dataset.tierUnlocked !== 'true');
      const firstVisibleIndex = nextIndex === -1 ? this.tiers.length : Math.max(0, nextIndex - 2);
      this.scrollToTier(firstVisibleIndex, 'auto');
    }

    scrollByTier(direction) {
      if (!this.viewport || !this.tiers.length) return;
      const tierWidth = this.tiers[0].getBoundingClientRect().width;
      const gap = Number.parseFloat(getComputedStyle(this.querySelector('.tier-discount-bar__tiers')).columnGap) || 0;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.viewport.scrollBy({ left: direction * (tierWidth + gap), behavior });
    }

    scrollToTier(index, behavior) {
      const tierWidth = this.tiers[0].getBoundingClientRect().width;
      const gap = Number.parseFloat(getComputedStyle(this.querySelector('.tier-discount-bar__tiers')).columnGap) || 0;
      const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.viewport.scrollTo({ left: Math.min(index * (tierWidth + gap), maximumScroll), behavior });
    }

    updateControls() {
      if (!this.viewport || !this.previousButton || !this.nextButton) return;
      const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.previousButton.disabled = this.viewport.scrollLeft <= 1;
      this.nextButton.disabled = this.viewport.scrollLeft >= maximumScroll - 1;
    }
  }

  customElements.define('tier-discount-bar', TierDiscountBar);
}
